/**
 * CollectionSettings - Settings modal for collection configuration
 *
 * Atomic level: Organism
 * Composes: Dialog, Switch, Label, Input
 *
 * Provides settings to enable/disable Regulatory Flags and edit the collection billing tag.
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../ui/dialog';
import { Switch } from '../../../ui/switch';
import { Label } from '../../../ui/label';
import { Input } from '../../../ui/input';
import { Button } from '../../../ui/button';
import { Settings } from 'lucide-react';

interface CollectionSettingsProps {
  complaintDetectionEnabled: boolean;
  vulnerabilityDetectionEnabled: boolean;
  billingTag: string | null;
  onComplaintDetectionChange: (enabled: boolean) => void;
  onVulnerabilityDetectionChange: (enabled: boolean) => void;
  onBillingTagSave: (billingTag: string | null) => Promise<void>;
  className?: string;
}

const normalizeBillingTag = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const CollectionSettings: React.FC<CollectionSettingsProps> = ({
  complaintDetectionEnabled,
  vulnerabilityDetectionEnabled,
  billingTag,
  onComplaintDetectionChange,
  onVulnerabilityDetectionChange,
  onBillingTagSave,
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const [draftBillingTag, setDraftBillingTag] = React.useState(billingTag ?? '');
  const [isSavingBillingTag, setIsSavingBillingTag] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setDraftBillingTag(billingTag ?? '');
    }
  }, [billingTag, open]);

  const normalizedBillingTag = normalizeBillingTag(draftBillingTag);
  const currentBillingTag = billingTag?.trim() ?? null;
  const hasBillingTagChanged = normalizedBillingTag !== currentBillingTag;

  const handleSaveBillingTag = async () => {
    setIsSavingBillingTag(true);
    try {
      await onBillingTagSave(normalizedBillingTag);
      setOpen(false);
    } catch {
      // Parent handler owns error feedback.
    } finally {
      setIsSavingBillingTag(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className={className} title="Collection settings">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Collection settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Complaint Detection Setting */}
          <div className="flex items-center justify-between rounded-2xl border border-border/70 bg-background/80 px-4 py-3">
            <Label htmlFor="complaint-detection" className="text-sm font-medium">
              Complaint Detection
            </Label>
            <Switch
              id="complaint-detection"
              checked={complaintDetectionEnabled}
              onCheckedChange={onComplaintDetectionChange}
            />
          </div>

          {/* Vulnerability Detection Setting */}
          <div className="flex items-center justify-between rounded-2xl border border-border/70 bg-background/80 px-4 py-3">
            <Label htmlFor="vulnerability-detection" className="text-sm font-medium">
              Vulnerability Detection
            </Label>
            <Switch
              id="vulnerability-detection"
              checked={vulnerabilityDetectionEnabled}
              onCheckedChange={onVulnerabilityDetectionChange}
            />
          </div>

          <div className="rounded-2xl border border-border/70 bg-background/80 px-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="billing-tag" className="text-sm font-medium">
                Billing tag
              </Label>
              <Input
                id="billing-tag"
                value={draftBillingTag}
                onChange={(event) => setDraftBillingTag(event.target.value)}
                placeholder="finance-ops"
              />
              <p className="text-xs text-muted-foreground">
                Used for internal usage attribution and reporting. Leave blank to clear.
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDraftBillingTag(billingTag ?? '')}
                disabled={!hasBillingTagChanged || isSavingBillingTag}
              >
                Reset
              </Button>
              <Button
                type="button"
                onClick={handleSaveBillingTag}
                disabled={!hasBillingTagChanged || isSavingBillingTag}
              >
                {isSavingBillingTag ? 'Saving...' : 'Save billing tag'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { CollectionSettings };
